import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Cloud, Rocket, X, ChevronDown, Check, AlertCircle, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { api, ApiError } from "@/lib/api";

const GRADIENT =
  "linear-gradient(135deg, rgba(134,87,241,0.18) 0%, rgba(107,63,214,0.08) 100%)";

// Mirrors backend validate_subdomain: lowercase alphanumerics + internal
// hyphens, 1–63 chars. Kept in sync with
// backend/app/services/cloud/digitalocean.py::_SUBDOMAIN_RE so the user
// gets instant feedback without a round-trip for obviously bad input.
const SUBDOMAIN_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

type DomainStatus =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "available"; fullDomain: string }
  | { kind: "taken"; reason: string; suggestions: string[] }
  | { kind: "invalid"; reason: string }
  | { kind: "error"; reason: string };

interface DOProject {
  id: string;
  name: string;
  is_default: boolean;
}

interface DeployConfirmData {
  app_name: string;
  app_type: string;
  provider: string;
  instance_size: string;
  estimated_cost: string;
  repo: string;
  branch: string;
  pending_key: string;
  do_projects?: DOProject[];
  deployment_domain?: string;
  suggested_subdomain?: string | null;
}

interface DeployConfirmWidgetProps {
  data: DeployConfirmData;
  onAction?: (actionId: string, context?: Record<string, unknown>) => void;
}

function sanitizeInput(raw: string): string {
  // Strip whitespace and lowercase as the user types; leave the rest
  // alone so the availability checker can produce a useful error
  // instead of silently mutating their input.
  return raw.trim().toLowerCase();
}

export function DeployConfirmWidget({ data, onAction }: DeployConfirmWidgetProps) {
  const projects = data.do_projects ?? [];
  const hasProjects = projects.length > 0;
  const deploymentDomain = data.deployment_domain ?? "daydra.app";

  // Default to the default project, or "new" if no projects
  const defaultProject = projects.find((p) => p.is_default);
  const [selectedProjectId, setSelectedProjectId] = useState<string>(
    hasProjects ? (defaultProject?.id ?? projects[0]?.id ?? "__new__") : "__new__",
  );
  const [projectOpen, setProjectOpen] = useState(false);

  const selectedProject = projects.find((p) => p.id === selectedProjectId);
  const projectLabel =
    selectedProjectId === "__new__"
      ? "Create new project"
      : selectedProject?.name ?? "Select project";

  // ── Domain field ────────────────────────────────────────────
  //
  // The widget ships with a pre-picked default subdomain (computed on
  // the backend and already verified free at build time). If the user
  // edits or clears the field, we re-check with a debounced call.
  const initialSubdomain = data.suggested_subdomain ?? "";
  const [subdomain, setSubdomain] = useState<string>(initialSubdomain);
  const [touched, setTouched] = useState(false);
  const [status, setStatus] = useState<DomainStatus>(
    initialSubdomain
      ? { kind: "available", fullDomain: `${initialSubdomain}.${deploymentDomain}` }
      : { kind: "idle" },
  );

  // Latest-wins check cancellation
  const requestIdRef = useRef(0);

  const runCheck = useCallback(
    async (candidate: string) => {
      const myId = ++requestIdRef.current;
      setStatus({ kind: "checking" });
      try {
        const res = await api.cloud.checkSubdomain(candidate);
        if (myId !== requestIdRef.current) return;
        if (res.available && res.full_domain) {
          setStatus({ kind: "available", fullDomain: res.full_domain });
        } else if (!res.available && res.reason) {
          // Reserved / bad format → "invalid", already-taken → "taken"
          const looksTaken = /already/i.test(res.reason);
          if (looksTaken) {
            setStatus({
              kind: "taken",
              reason: res.reason,
              suggestions: res.suggestions ?? [],
            });
          } else {
            setStatus({ kind: "invalid", reason: res.reason });
          }
        } else {
          setStatus({ kind: "error", reason: "Couldn't check that domain." });
        }
      } catch (err) {
        if (myId !== requestIdRef.current) return;
        const msg =
          err instanceof ApiError
            ? err.message
            : "Network error — try again.";
        setStatus({ kind: "error", reason: msg });
      }
    },
    [],
  );

  // Debounced live check when the user types
  useEffect(() => {
    if (!touched) return;
    if (!subdomain) {
      setStatus({ kind: "idle" });
      return;
    }
    if (!SUBDOMAIN_RE.test(subdomain)) {
      setStatus({
        kind: "invalid",
        reason:
          "Lowercase letters, numbers, and hyphens only. Can't start or end with a hyphen.",
      });
      return;
    }
    const handle = setTimeout(() => {
      void runCheck(subdomain);
    }, 350);
    return () => clearTimeout(handle);
  }, [subdomain, touched, runCheck]);

  const pickSuggestion = useCallback(
    (candidate: string) => {
      // Suggestions come back as fully-qualified names when taken;
      // strip the domain suffix before using them.
      const stripped = candidate.endsWith(`.${deploymentDomain}`)
        ? candidate.slice(0, -1 - deploymentDomain.length)
        : candidate;
      setSubdomain(stripped);
      setTouched(true);
    },
    [deploymentDomain],
  );

  // ── Domain status UI helpers ────────────────────────────────

  const statusLine = useMemo(() => {
    switch (status.kind) {
      case "idle":
        return null;
      case "checking":
        return (
          <span className="flex items-center gap-1 text-daydra-muted">
            <Loader2 size={11} className="animate-spin" />
            Checking…
          </span>
        );
      case "available":
        return (
          <span className="flex items-center gap-1 text-emerald-400">
            <Check size={11} />
            Available
          </span>
        );
      case "invalid":
      case "error":
        return (
          <span className="flex items-center gap-1 text-amber-400">
            <AlertCircle size={11} />
            {status.reason}
          </span>
        );
      case "taken":
        return (
          <span className="flex items-center gap-1 text-amber-400">
            <AlertCircle size={11} />
            Taken
          </span>
        );
    }
  }, [status]);

  const canDeploy =
    // Empty field is fine — the user chose to skip a custom domain.
    !subdomain ||
    status.kind === "available" ||
    // The user left the pre-filled suggestion untouched.
    (!touched && !!initialSubdomain);

  const handleDeploy = () => {
    const payload: Record<string, unknown> = {
      pending_key: data.pending_key,
      do_project_id: selectedProjectId,
    };
    // Only send a subdomain if we actually have one in an "OK" state.
    if (subdomain && canDeploy) {
      payload.subdomain = subdomain;
    }
    onAction?.("confirm_deploy", {
      __action: "confirm_deploy",
      __dismiss: true,
      __payload: payload,
    });
  };

  return (
    <Card
      variant="default"
      padding="default"
      className="max-w-sm animate-scale-in"
    >
      {/* Header with gradient — matches Card's rounded-xl corners */}
      <div
        className="rounded-t-xl -mx-4 -mt-4 px-4 pt-4 pb-3 mb-3 overflow-hidden"
        style={{ background: GRADIENT }}
      >
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-daydra-accent/15">
            <Cloud size={16} className="text-daydra-accent" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-daydra-text">
              Deploy to {data.provider}
            </h3>
            <p className="text-2xs text-daydra-muted">{data.repo}</p>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-1.5 mb-3">
        <div className="flex justify-between text-xs">
          <span className="text-daydra-muted">App type</span>
          <span className="text-daydra-dim capitalize">{data.app_type}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-daydra-muted">Branch</span>
          <span className="text-daydra-dim font-mono">{data.branch}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-daydra-muted">Instance</span>
          <span className="text-daydra-dim">{data.instance_size}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-daydra-muted">Estimated cost</span>
          <span className="text-daydra-text font-medium">{data.estimated_cost}</span>
        </div>
      </div>

      {/* DO Project selector */}
      <div className="mb-3">
        <p className="text-2xs text-daydra-muted mb-1.5">
          {data.provider} project
        </p>
        <div className="relative">
          <button
            onClick={() => setProjectOpen(!projectOpen)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-white/[0.06] bg-daydra-raised text-sm text-daydra-dim hover:text-daydra-text transition-colors"
          >
            <span className="truncate">{projectLabel}</span>
            <ChevronDown
              size={14}
              className={cn(
                "shrink-0 text-daydra-muted transition-transform",
                projectOpen && "rotate-180",
              )}
            />
          </button>
          {projectOpen && (
            <div className="absolute z-10 mt-1 w-full rounded-lg border border-white/[0.06] bg-daydra-surface shadow-lg overflow-y-auto max-h-48">
              {projects.map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    setSelectedProjectId(p.id);
                    setProjectOpen(false);
                  }}
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm transition-colors",
                    selectedProjectId === p.id
                      ? "bg-daydra-accent/10 text-daydra-text"
                      : "text-daydra-dim hover:bg-white/[0.04] hover:text-daydra-text",
                  )}
                >
                  {p.name}
                  {p.is_default && (
                    <span className="ml-1.5 text-2xs text-daydra-muted">(default)</span>
                  )}
                </button>
              ))}
              <button
                onClick={() => {
                  setSelectedProjectId("__new__");
                  setProjectOpen(false);
                }}
                className={cn(
                  "w-full text-left px-3 py-2 text-sm border-t border-white/[0.04] transition-colors",
                  selectedProjectId === "__new__"
                    ? "bg-daydra-accent/10 text-daydra-text"
                    : "text-daydra-dim hover:bg-white/[0.04] hover:text-daydra-text",
                )}
              >
                + Create new project
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Custom domain */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-2xs text-daydra-muted">Custom domain</p>
          <div className="text-2xs">{statusLine}</div>
        </div>
        <div
          className={cn(
            "flex items-stretch rounded-lg border bg-daydra-raised overflow-hidden transition-colors",
            status.kind === "available" && "border-emerald-500/30",
            (status.kind === "taken" ||
              status.kind === "invalid" ||
              status.kind === "error") &&
              "border-amber-500/30",
            status.kind === "checking" && "border-white/[0.1]",
            status.kind === "idle" && "border-white/[0.06]",
          )}
        >
          <input
            type="text"
            value={subdomain}
            placeholder="your-app"
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            onChange={(e) => {
              setTouched(true);
              setSubdomain(sanitizeInput(e.target.value));
            }}
            className="flex-1 min-w-0 bg-transparent px-3 py-2 text-sm text-daydra-text placeholder:text-daydra-muted focus:outline-none"
          />
          <div className="flex items-center pr-3 text-xs text-daydra-muted select-none font-mono">
            .{deploymentDomain}
          </div>
        </div>
        {status.kind === "taken" && status.suggestions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {status.suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => pickSuggestion(s)}
                className="px-2 py-0.5 rounded-md border border-white/[0.08] bg-white/[0.03] text-2xs text-daydra-dim hover:text-daydra-text hover:border-daydra-accent/40 transition-colors font-mono"
              >
                {s}
              </button>
            ))}
          </div>
        )}
        {!subdomain && (
          <p className="text-2xs text-daydra-muted mt-1.5">
            Leave blank to skip — you can set one later.
          </p>
        )}
      </div>

      <p className="text-2xs text-daydra-muted mb-3">
        Billed directly to your {data.provider} account.
      </p>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={!canDeploy}
          className="flex-1 gap-1.5"
          onClick={handleDeploy}
        >
          <Rocket size={14} />
          Deploy
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="flex-1 gap-1.5"
          onClick={() =>
            onAction?.("cancel_deploy", {
              __action: "cancel_deploy",
              __dismiss: true,
              __payload: { pending_key: data.pending_key },
            })
          }
        >
          <X size={14} />
          Cancel
        </Button>
      </div>
    </Card>
  );
}
